import { ChangeEvent, useState } from 'react'

import { Checkbox } from '@joor/design-system'
import isEmpty from 'lodash/isEmpty'
import { useSelector } from 'react-redux'

import { PendoEvent } from 'types'
import { fromGlobalId } from 'utils/transformations/graphql'

import { useRenderToasters } from 'hooks/useRenderToasters'
import { getAccountId } from 'store/currentUser/selectors'

import { useGetLiteRetailerAccountsInfo } from '../../NavbarLiteRetailer.queries'
import { messages } from './constants'
import { useUpdateAccountsPrivacy } from 'features/Navbar/NavbarLiteRetailer/NavbarLiteRetailer.mutations'
import {
  AccountsSelectionModal,
  mapToAccountOptions,
} from 'features/Shared/modals/AccountsSelectionModal/AccountsSelectionModal'

type ModalProps = {
  isOpen: boolean
  onClose?: () => void
}

const AccountsVisibilityModal = ({
  isOpen,
  onClose = () => {},
}: ModalProps) => {
  const { accounts, accountsPrivacyLoading } = useGetLiteRetailerAccountsInfo()

  const accountId = useSelector(getAccountId)
  const { updateAccountsPrivacy } = useUpdateAccountsPrivacy()
  const { renderSuccessToast, renderErrorToast } = useRenderToasters()

  // None primary accounts can be hidden
  const selectableAccounts = accounts.filter((item) => !item.isPrimary)

  const checkAccountsDataChecked = selectableAccounts
    ? Object.fromEntries(selectableAccounts.map((item) => [item.id, true]))
    : {}
  const checkAccountsDataUnchecked = selectableAccounts
    ? Object.fromEntries(selectableAccounts.map((item) => [item.id, false]))
    : {}

  /**
   * If privacy === false => hidden (checkbox checked),
   * if privacy === true => visible (checkbox unchecked)
   */
  const checkAccountPrivacy = (idToCompare: string) => {
    const account = selectableAccounts?.find(
      (account) => account.id === idToCompare,
    )

    return !account?.privacy
  }

  const accountsPrivacyChecked =
    selectableAccounts?.reduce(
      (acc, item) => {
        acc[item.id] = checkAccountPrivacy(item.id)
        return acc
      },
      {} as {
        [key: string]: boolean
      },
    ) || {}

  const allCheckedInitialState =
    selectableAccounts.length > 0 &&
    Object.values(accountsPrivacyChecked).every((privacy) => privacy)

  const [allAccountsChecked, setAllAccountsChecked] = useState(
    allCheckedInitialState,
  )
  const [accountsChecked, setAccountsChecked] = useState(
    selectableAccounts?.length
      ? accountsPrivacyChecked
      : checkAccountsDataChecked,
  )

  const handleHideAccountCheckbox = (
    event: ChangeEvent<HTMLInputElement>,
    userAccountId: string,
  ) => {
    if (!event.target.checked && allAccountsChecked) {
      setAllAccountsChecked(false)
    }

    if (
      Object.values(accountsChecked).filter((check) => check).length ===
        Object.keys(accountsChecked).length - 1 &&
      event.target.checked
    ) {
      setAllAccountsChecked(true)
    }

    setAccountsChecked({
      ...accountsChecked,
      [userAccountId]: event.target.checked,
    })
  }

  const saveAccounts = () => {
    const changedAccounts = Object.entries(accountsChecked)
      .filter(([id, isChecked]) => {
        const userAccount = selectableAccounts.find((u) => u.id === id)
        const privacy = userAccount && checkAccountPrivacy(userAccount.id)
        return isChecked !== privacy
      })
      .map(([id, isChecked]) => ({
        accountId: id,
        privacy: !isChecked,
      }))

    if (changedAccounts.length) {
      const accountsHidden = changedAccounts.reduce((num, account) => {
        return account.privacy ? num : num + 1
      }, 0)
      if (accountsHidden > 0) {
        window.pendo.track(PendoEvent.Account_Visibility_Modal, {
          accountsHidden,
        })
      }
      updateAccountsPrivacy(changedAccounts).then((resp) => {
        if (resp.errors && !isEmpty(resp.errors)) {
          renderErrorToast({
            title: messages.errorTitle,
            description: messages.errorDescription,
          })
        } else {
          renderSuccessToast({
            title: messages.successTitle,
            description: messages.successDescription,
          })
        }
      })
    }
    onClose()
  }

  const accountOptions = mapToAccountOptions(accounts, (account) => ({
    id: account.id,
    disableOptionCheckbox: account.isPrimary,
    accountName: `${account.name} (${fromGlobalId(account.id).id})`,
    isPrimary: account.isPrimary,
    showCheckMarkIcon: String(accountId) === fromGlobalId(account.id).id,
    privacy: account.privacy,
    createdAt: account.createdAt.toString(),
    connectedAccounts: account.connectedAccounts,
    createdBy:
      account.accountCreationSource?.requestedByAccount &&
      account.accountCreationSource.requestedByAccount.id === account.id
        ? 'you'
        : account.accountCreationSource?.requestedByAccount?.name,
    disabled: account.isPrimary,
    showPrimaryFooterText: account.isPrimary,
    optionChecked: accountsChecked[account.id] ?? false,
  }))

  return (
    <AccountsSelectionModal
      isOpen={isOpen}
      title={messages.title}
      subtitle={messages.subtitle}
      onClose={onClose}
      headingContent={messages.headingContent}
      accountOptions={accountOptions}
      isLoading={accountsPrivacyLoading}
      onCheckboxChange={handleHideAccountCheckbox}
      okButton={{
        label: messages.saveButton,
        onClick: saveAccounts,
      }}
      leftExtraAction={
        <Checkbox
          id={'check-all-accounts'}
          label={messages.allAccounts}
          onChange={(event) => {
            setAccountsChecked(
              event.target.checked
                ? checkAccountsDataChecked
                : checkAccountsDataUnchecked,
            )
            setAllAccountsChecked(event.target.checked)
          }}
          size="regular"
          checked={allAccountsChecked}
        />
      }
    />
  )
}

export default AccountsVisibilityModal
